import SoundSelector from "@/components/UI/SoundSelector";
import Toggles from "@/components/UI/Toggles";
import Tooltip from "@/components/UI/Tooltip";
import {
  type NotificationChannel,
  type NotificationGroupKey,
  type NotificationStatusKey,
  type StatusNotificationToggles,
  SOUND_OPTIONS,
} from "@/lib/notificationConfig";

type MasterChannel = "sound" | "tray" | "browser" | null;

interface NotificationGroupColumnProps {
  groupKey: NotificationGroupKey;
  label: string;
  statuses: NotificationStatusKey[];
  groupSound: string;
  statusPreferences: Record<string, StatusNotificationToggles>;
  globalSoundDisabled: boolean;
  globalBellDisabled: boolean;
  globalBrowserDisabled: boolean;
  onHighlightMaster: (channel: MasterChannel) => void;
  onDisabledToggleClick: (channel: MasterChannel) => void;
  onGroupSoundChange: (soundFile: string) => void;
  onStatusToggle: (status: string, channel: NotificationChannel) => void;
  onToggleAll: (channel: NotificationChannel, enabled: boolean) => void;
}

const MASTER_DISABLED_SOUND =
  "Disabled by master sound toggle at the top of the page";
const MASTER_DISABLED_TRAY =
  "Disabled by master notifications toggle at the top of the page";
const MASTER_DISABLED_BROWSER =
  "Disabled by master browser toggle at the top of the page";

const ROW_GRID =
  "grid grid-cols-[1fr_2.75rem_2.75rem_2.75rem] items-center gap-2 px-3";

const SpeakerIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={className}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z"
    />
  </svg>
);

const BellIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={className}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
    />
  </svg>
);

const GlobeIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={className}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418"
    />
  </svg>
);

interface ChannelToggleProps {
  checked: boolean;
  masterChannel: MasterChannel;
  globalDisabled: boolean;
  disabledTooltip: string;
  onToggle: () => void;
  onHighlightMaster: (channel: MasterChannel) => void;
  onDisabledToggleClick: (channel: MasterChannel) => void;
}

function ChannelToggle({
  checked,
  masterChannel,
  globalDisabled,
  disabledTooltip,
  onToggle,
  onHighlightMaster,
  onDisabledToggleClick,
}: ChannelToggleProps) {
  return (
    <div
      className="flex justify-center"
      onMouseEnter={() =>
        globalDisabled ? onHighlightMaster(masterChannel) : undefined
      }
      onMouseLeave={() => onHighlightMaster(null)}
      onClickCapture={
        globalDisabled
          ? (e) => {
              e.stopPropagation();
              e.preventDefault();
              onDisabledToggleClick(masterChannel);
            }
          : undefined
      }
    >
      {globalDisabled ? (
        <Tooltip text={disabledTooltip} showIcon={false}>
          <Toggles checked={checked} handleChange={onToggle} disabled={true} />
        </Tooltip>
      ) : (
        <Toggles checked={checked} handleChange={onToggle} />
      )}
    </div>
  );
}

function NotificationGroupColumn({
  groupKey,
  label,
  statuses,
  groupSound,
  statusPreferences,
  globalSoundDisabled,
  globalBellDisabled,
  globalBrowserDisabled,
  onHighlightMaster,
  onDisabledToggleClick,
  onGroupSoundChange,
  onStatusToggle,
  onToggleAll,
}: NotificationGroupColumnProps) {
  const enabledCount = statuses.filter((s) => {
    const prefs = statusPreferences[s];
    if (!prefs) return false;
    const effectiveSound = prefs.sound && !globalSoundDisabled;
    const effectiveBell = prefs.bell && !globalBellDisabled;
    const effectiveBrowser = prefs.browser && !globalBrowserDisabled;
    return effectiveSound || effectiveBell || effectiveBrowser;
  }).length;

  const allSoundEnabled = statuses.every((s) => statusPreferences[s]?.sound);
  const allBellEnabled = statuses.every((s) => statusPreferences[s]?.bell);
  const allBrowserEnabled = statuses.every(
    (s) => statusPreferences[s]?.browser,
  );

  const countBadgeClass =
    enabledCount > 0
      ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
      : "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400";

  return (
    <div
      data-testid={`NotificationGroupColumn-${groupKey}`}
      id={`NotificationGroupColumn-${groupKey}`}
      className="flex flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700"
    >
      {/* Column Title Row */}
      <div className="flex items-center justify-between gap-2 bg-gray-50 px-3 py-2 dark:bg-gray-800">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {label}
          </span>
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-medium ${countBadgeClass}`}
          >
            {enabledCount}/{statuses.length}
          </span>
        </div>
        <SoundSelector
          value={groupSound}
          onChange={onGroupSoundChange}
          options={SOUND_OPTIONS}
        />
      </div>

      {/* STATUS Header Row */}
      <div
        className={`${ROW_GRID} border-t border-gray-200 bg-gray-100/60 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400`}
      >
        <div>Status</div>
        <Tooltip text="Play a sound when a status changes" showIcon={false}>
          <SpeakerIcon className="mx-auto h-4 w-4 cursor-help" />
        </Tooltip>
        <Tooltip text="Show alerts in the notifications panel" showIcon={false}>
          <BellIcon className="mx-auto h-4 w-4 cursor-help" />
        </Tooltip>
        <Tooltip text="Push notifications via the browser" showIcon={false}>
          <GlobeIcon className="mx-auto h-4 w-4 cursor-help" />
        </Tooltip>
      </div>

      {/* Toggle All Row */}
      <div
        data-testid={`NotificationGroupColumn-${groupKey}-ToggleAllRow`}
        className={`${ROW_GRID} border-t border-gray-200 bg-gray-50/50 py-1.5 text-xs font-medium italic text-gray-500 dark:border-gray-700 dark:bg-gray-800/30 dark:text-gray-400`}
      >
        <div>Toggle All</div>
        <ChannelToggle
          checked={allSoundEnabled}
          masterChannel="sound"
          globalDisabled={globalSoundDisabled}
          disabledTooltip={MASTER_DISABLED_SOUND}
          onToggle={() => onToggleAll("sound", !allSoundEnabled)}
          onHighlightMaster={onHighlightMaster}
          onDisabledToggleClick={onDisabledToggleClick}
        />
        <ChannelToggle
          checked={allBellEnabled}
          masterChannel="tray"
          globalDisabled={globalBellDisabled}
          disabledTooltip={MASTER_DISABLED_TRAY}
          onToggle={() => onToggleAll("bell", !allBellEnabled)}
          onHighlightMaster={onHighlightMaster}
          onDisabledToggleClick={onDisabledToggleClick}
        />
        <ChannelToggle
          checked={allBrowserEnabled}
          masterChannel="browser"
          globalDisabled={globalBrowserDisabled}
          disabledTooltip={MASTER_DISABLED_BROWSER}
          onToggle={() => onToggleAll("browser", !allBrowserEnabled)}
          onHighlightMaster={onHighlightMaster}
          onDisabledToggleClick={onDisabledToggleClick}
        />
      </div>

      {/* Status Rows */}
      {statuses.map((status) => {
        const prefs = statusPreferences[status];
        if (!prefs) return null;
        return (
          <div
            key={status}
            data-testid={`StatusNotificationRow-${status}`}
            id={`StatusNotificationRow-${status}`}
            className={`${ROW_GRID} border-t border-gray-100 py-1.5 text-sm text-gray-700 dark:border-gray-700/50 dark:text-gray-300`}
          >
            <div className="truncate" title={status}>
              {status}
            </div>
            <ChannelToggle
              checked={prefs.sound}
              masterChannel="sound"
              globalDisabled={globalSoundDisabled}
              disabledTooltip={MASTER_DISABLED_SOUND}
              onToggle={() => onStatusToggle(status, "sound")}
              onHighlightMaster={onHighlightMaster}
              onDisabledToggleClick={onDisabledToggleClick}
            />
            <ChannelToggle
              checked={prefs.bell}
              masterChannel="tray"
              globalDisabled={globalBellDisabled}
              disabledTooltip={MASTER_DISABLED_TRAY}
              onToggle={() => onStatusToggle(status, "bell")}
              onHighlightMaster={onHighlightMaster}
              onDisabledToggleClick={onDisabledToggleClick}
            />
            <ChannelToggle
              checked={prefs.browser}
              masterChannel="browser"
              globalDisabled={globalBrowserDisabled}
              disabledTooltip={MASTER_DISABLED_BROWSER}
              onToggle={() => onStatusToggle(status, "browser")}
              onHighlightMaster={onHighlightMaster}
              onDisabledToggleClick={onDisabledToggleClick}
            />
          </div>
        );
      })}
    </div>
  );
}

export default NotificationGroupColumn;
